import * as React from "react";
import { Form } from "react-bootstrap";
import { shallowEqual } from "react-redux";

import { useAppSelector } from "../../../redux/store";
import { OscillometryMeasurement } from "../../../Types/Common/MeasurementTypes";
import { updateMeasurementWithBody } from "../../../libs/trialsLib";
import { PatientID } from "../../../Types/Common/BrandedTypes";

import "../../../components/SeriesNotesComponent.css";

const { useState, useEffect, useRef, useCallback } = React;

interface OscillometryNotesProps {
  patientID: PatientID | undefined;
  bestMeasurements: OscillometryMeasurement | undefined;
  refetchMeasurements: () => void | Promise<void>;
}

const OscillometryNotes: React.FC<OscillometryNotesProps> = ({ patientID, bestMeasurements, refetchMeasurements }) => {
  const translationText = useAppSelector(state => state.translations, shallowEqual);
  const authenticatedProvider = useAppSelector(state => state.provider, shallowEqual);
  const isReadOnly = authenticatedProvider.viewingAs?.readOnly;

  const editTimeThreshold = 800;
  const showChangesSavedDuration = 5000;

  const [notes, setNotes] = useState(bestMeasurements?.notes === undefined ? "" : bestMeasurements.notes.trim());
  const [lastEditTime, setLastEditTime] = useState(0);
  const [showChangesSaved, setShowChangesSaved] = useState(false);

  const updateNotes = useCallback(async () => {
    if (!bestMeasurements?.measurementID || !patientID) return;

    setShowChangesSaved(true);
    setTimeout(() => setShowChangesSaved(false), showChangesSavedDuration);

    try {
      await updateMeasurementWithBody({
        patientID,
        measurementID: bestMeasurements.measurementID,
        notes: notes,
      });
      await refetchMeasurements();
    } catch (e) {
      console.error('Error saving oscillometry notes:', e);
    }
  }, [bestMeasurements, notes, patientID, refetchMeasurements]);

  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    setLastEditTime(Date.now());
    const timer = setTimeout(() => {
      if (Date.now() - lastEditTime > editTimeThreshold &&
        notes.trim() !== (bestMeasurements?.notes ?? '').trim()) {
        updateNotes();
      }
    }, editTimeThreshold);
    return () => clearTimeout(timer);
  }, [notes]);

  return (
    <div className="SeriesNotesComponent">
      <p className="series-notes-title">{translationText.seriesNotes || 'Notes'}</p>
      <div className="series-notepad-container">
        <div className="series-notepad">
          <Form.Group className="series-notepad-formgroup" controlId="osc-notes">
            <Form.Control
              className="series-notepad-formcontrol"
              value={notes}
              as="textarea"
              spellCheck="false"
              onChange={e => isReadOnly ? undefined : setNotes(e.target.value)}
              style={{ height: 170 }}
              disabled={isReadOnly}
            />
          </Form.Group>
          {showChangesSaved && (
            <span className="series-span-changes-saved text-success">
              {"\u2714"}{` ${translationText.changesSaved}`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default OscillometryNotes;